import { Box } from '@iheartjane/reefer';
import { createContext, useContext, useState } from 'react';
import type { PropsWithChildren } from 'react';

import { useKioskSpacing } from '../../../hooks';
import { SpecialDetailCallout } from './SpecialDetailCallout';
import { SpecialDetailFooter } from './SpecialDetailFooter';
import { SpecialDetailHeader } from './SpecialDetailHeader';
import { SpecialDetailLayout } from './SpecialDetailLayout';
import { SpecialDetailProgress } from './SpecialDetailProgress';

const SpecialDetailContext = createContext<{
  disableAnimation?: boolean;
  scrolledFromTop?: boolean;
}>({
  disableAnimation: false,
  scrolledFromTop: false,
});

export const useSpecialDetailContext = () => useContext(SpecialDetailContext);

/* eslint-disable @iheartjane/workspace/no-dom-globals-in-react-fc */
const SpecialDetailContainer = ({ children }: PropsWithChildren) => {
  const { isIpad } = useKioskSpacing();
  const [scrolledFromTop, setScrolledFromTop] = useState(false);
  const [disableAnimation] = useState(isIpad);
  const specialDetailHeader = document.querySelector(
    '#special-detail-layout-header'
  );
  const [placeholderHeight, setPlaceholderHeight] = useState(
    specialDetailHeader?.clientHeight || 0
  );

  /**
   * This intersection observer watches when our anchor element is moved. This element is our signal to apply/remove the class we use to style the downstream elements.
   */
  if (
    'IntersectionObserver' in window &&
    'IntersectionObserverEntry' in window &&
    'intersectionRatio' in window.IntersectionObserverEntry.prototype &&
    !disableAnimation
  ) {
    const intersectionObserver = new IntersectionObserver((entries) => {
      const wrapperElement = document.querySelector('#special-detail-layout');

      if (!wrapperElement) {
        return;
      }

      if (entries[0].boundingClientRect.y < -1) {
        wrapperElement.classList.add('scrolled-from-top');
        setScrolledFromTop(true);
      } else {
        wrapperElement.classList.remove('scrolled-from-top');
        setScrolledFromTop(false);
      }
    });

    const targetElement = document.querySelector(
      '#special-detail-scroll-anchor'
    );

    if (targetElement) {
      intersectionObserver.observe(targetElement);
    }
  }

  /**
   * This resize observer watches the size of the header to ensure there's the correct amount of
   * space pushing down the actual page content. We need to do this because position fixed takes the header out of the document flow & using position sticky made the transition very flickery.
   */
  if (
    'ResizeObserver' in window &&
    'ResizeObserverEntry' in window &&
    !disableAnimation
  ) {
    const resizeObserver = new ResizeObserver((entries) => {
      setPlaceholderHeight(entries[0].contentRect.height + 32);
    });

    if (specialDetailHeader) {
      resizeObserver.observe(specialDetailHeader);
    }
  }

  return (
    <SpecialDetailContext.Provider
      value={{
        disableAnimation,
        scrolledFromTop: disableAnimation ? true : scrolledFromTop,
      }}
    >
      <Box
        width="100%"
        id="special-detail-layout"
        pb={placeholderHeight as any}
      >
        <Box
          height={placeholderHeight}
          data-testid="special-detail-layout-header-placeholder"
        />
        {children}
        <Box
          id="special-detail-scroll-anchor"
          height="1px"
          width="1px"
          position="absolute"
          top={0}
          left={0}
        />
      </Box>
    </SpecialDetailContext.Provider>
  );
};
/* eslint-enable @iheartjane/workspace/no-dom-globals-in-react-fc */

export const SpecialDetail = Object.assign(SpecialDetailContainer, {
  Layout: SpecialDetailLayout,
  Header: SpecialDetailHeader,
  Progress: SpecialDetailProgress,
  Callout: SpecialDetailCallout,
  Footer: SpecialDetailFooter,
});
